import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'react-bootstrap-icons';
import Shade1 from '../assets/NewHome/shade1.png';
import Shade2 from '../assets/NewHome/shade2.png';

import slide1 from '../assets/NewHome/slide1.png';
import slide2 from '../assets/NewHome/slide2.png'; 
import EarlyAccessForm from './EarlyAccessForm';

const InitialSectionContent = () => {
  const sectionRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showFreeAccountForm, setShowFreeAccountForm] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight || 1;

      // progress: 0 when section just enters viewport, 1 when it has scrolled one viewport height
      const visibleTop = windowHeight - rect.top;
      const progress = Math.min(Math.max(visibleTop / windowHeight, 0), 2);
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Parallax offsets: slide1 moves first, slide2 lags behind then catches up
  const slide1Offset = -150 * scrollProgress; // moves earlier / faster
  const slide2Offset = -250 * Math.max(scrollProgress - 0.3, 0); // starts later

  return (
    <>
      <style>{`
        .cta-button {
          position: relative;
          overflow: hidden;
          border: 1px solid rgb(94 170 158 / 80%);;
          transition: all 0.3s ease;
        }
        
        .cta-button:hover {
          border-color: rgb(94 170 158 / 80%);
        }
        
        .arrow-icon {
          opacity: 0;
          transform: translateX(-40px);
          transition: all 0.4s ease;
        }
        
        .cta-button:hover .arrow-icon {
          opacity: 1;
          transform: translateX(0);
        }
        
        .login-button {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.3);
          transition: all 0.3s ease;
        }
        
        .login-button:hover {
          border-color: rgba(255, 255, 255, 0.6);
        }
        
        @keyframes moveLeftToRight {
          0% {
            transform: translateX(-100%);
          }
          20% {
            transform: translateX(50%);
          }
          40% {
            transform: translateX(80%);
          }
          60% {
            transform: translateX(120%);
          }
          80% {
            transform: translateX(180%);
          }
          90% {
            transform: translateX(210%);
          }
          100% {
            transform: translateX(200%);
          }
        }
        
        @keyframes moveRightToLeft {
          0% {
            transform: translateX(200%);
          }
          20% {
            transform: translateX(50%);
          }
          40% {
            transform: translateX(20%);
          }
          60% {
            transform: translateX(-20%);
          }
          80% {
            transform: translateX(-80%);
          }
          90% {
            transform: translateX(-110%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        
        .shade-animation-left {
          animation: moveLeftToRight 15s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
          mix-blend-mode: soft-light;
          filter: blur(2px) brightness(0.8) contrast(1.2);
          mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 20%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.8) 80%, rgba(0,0,0,0.2) 95%, transparent 100%);
          -webkit-mask-image: linear-gradient(to right, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 20%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.8) 80%, rgba(0,0,0,0.2) 95%, transparent 100%);
        }
        
        .shade-animation-right {
          animation: moveRightToLeft 15s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
          mix-blend-mode: soft-light;
          filter: blur(2px) brightness(0.8) contrast(1.2);
          mask-image: linear-gradient(to left, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 20%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.8) 80%, rgba(0,0,0,0.2) 95%, transparent 100%);
          -webkit-mask-image: linear-gradient(to left, transparent 0%, rgba(0,0,0,0.2) 5%, rgba(0,0,0,0.8) 20%, rgba(0,0,0,1) 50%, rgba(0,0,0,0.8) 80%, rgba(0,0,0,0.2) 95%, transparent 100%);
        }
      `}</style>
      <div 
        ref={sectionRef}
        className="rounded-[35px] pl-6 sm:pl-10 md:pl-14 lg:pl-20 xl:pl-20 py-10 sm:py-12 md:py-16 lg:py-20 xl:py-24 2xl:py-28 flex flex-col lg:flex-row items-start gap-10 relative overflow-hidden h-auto"
        style={{
          background: 'linear-gradient(120deg, #0e1016 10%, #14131d 30%, #1c1b34 55%, #2c1f58 75%, #3c2991 100%)',
          maxHeight: '90vh',
          minHeight: 'clamp(400px, 50vh, 700px)'
        }}
      >
        {/* Animated Background Images - Both shades blending together */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
          {/* Shade1 moving left to right */}
          <img 
            src={Shade1} 
            alt="" 
            className="shade-animation-left absolute top-0 bottom-0"
            style={{ 
              height: '100%', 
              width: 'auto',
              maxWidth: 'none',
              left: '0',
              objectFit: 'cover',
              opacity: '0.25'
            }}
          />
          
          {/* Shade2 moving right to left */}
          <img 
            src={Shade2} 
            alt="" 
            className="shade-animation-right absolute top-0 bottom-0"
            style={{ 
              height: '100%', 
              width: 'auto',
              maxWidth: 'none',
              right: '0',
              objectFit: 'cover',
              opacity: '0.25'
            }}
          />
          
          {/* Gradient overlay to blend everything with background - matching exact background colors */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'linear-gradient(120deg, rgba(14, 16, 22, 0.6) 0%, rgba(20, 19, 29, 0.4) 10%, rgba(28, 27, 52, 0.2) 30%, transparent 40%, transparent 60%, rgba(28, 27, 52, 0.2) 70%, rgba(44, 31, 88, 0.4) 85%, rgba(60, 41, 145, 0.6) 100%)'
            }}
          />
          
          {/* Additional soft overlay to further blend */}
          <div 
            className="absolute inset-0 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse at center, transparent 0%, rgba(14, 16, 22, 0.3) 100%)'
            }}
          />
        </div>
        
      <div className="max-w-xl lg:max-w-2xl xl:max-w-3xl text-left relative z-10 w-full lg:w-1/2">
        <h1
          className="text-3xl sm:text-4xl md:text-[2.75rem] lg:text-4xl xl:text-5xl  font-normal text-white mb-3 sm:mb-4 md:mb-4 lg:mb-5 xl:mb-4 leading-tight"
          style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
        >
          Win more deals.<br /> Be more billable.
        </h1>
        
        <h2
          className="text-base sm:text-lg md:text-xl lg:text-xl xl:text-2xl text-white mb-3 sm:mb-4 md:mb-4 lg:mb-5 xl:mb-12 font-light"
          style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
        >
          AI Sales Platform for consulting firms
        </h2>
        
        <p
          className="text-sm sm:text-base md:text-base lg:text-base xl:text-base font-extralight text-[#E3E5F8] leading-relaxed mb-6 sm:mb-7 md:mb-8 lg:mb-9 xl:mb-10 max-w-lg lg:max-w-xl xl:max-w-md"
          style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}
        >
          From discovering new leads, researching opportunities, to creating proposals, our AI agents automate the sales process, while you focus on delivery.
        </p>
        
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowFreeAccountForm(true);
            }}
            onTouchStart={(e) => {
              e.stopPropagation();
            }}
            className="cta-button inline-flex items-center justify-center gap-3 rounded-full pr-2 pl-10 py-1 text-xs sm:text-sm md:text-sm lg:text-base xl:text-sm font-light text-white touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <span className="font-extralight">Get your free account</span>
            <span className="arrow-icon inline-flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-white rounded-full">
              <ArrowRight className="text-white" size={16} />
            </span>
          </button>
        </div>
        
        <p className="text-xs sm:text-xs md:text-sm lg:text-sm xl:text-sm text-[#7AA4FF] font-extralight mt-5 sm:mt-6 md:mt-7 lg:mt-8" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
          No credit card required.
        </p>
      </div>

      {/* Right side parallax slides */}
      {/* Slide 1 - back, moves first */}
      <img
        src={slide2}
        alt=""
        className="absolute -right-[500px] -bottom-[450px] w-[1000px] rounded-2xl z-10"
        style={{
          transform: `translateY(${slide1Offset}px)`,
          transition: 'transform 0.1s linear',
        }}
      />
      {/* Slide 2 - front, starts later */}
      <img
        src={slide1}
        alt=""
        className="absolute -right-[600px] -bottom-[400px] z-20 rounded-2xl"
        style={{
          transform: `translateY(${slide2Offset}px)`,
          transition: 'transform 0.1s linear',
        }}
      />
        </div>

        {/* Free account modal */}
        {showFreeAccountForm && (
          <div
            className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
            onClick={() => setShowFreeAccountForm(false)}
            style={{ 
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain'
            }}
          >
            <div 
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-full my-auto"
            >
              <EarlyAccessForm />
            </div>
          </div>
        )}
    </>
  );
};

export default InitialSectionContent;

